import { ChatRequest, CurrentMonthTokenBillingStatus, getCurrentMonthTokenBillingStatus } from '@/api/llm';
import { userIsProtomiaPro } from '@/api/auth';
import * as ProtomiaApi from '@/api/protomia';
import { JoinRole } from '@/api/admin';
import { llmModelListForUsers, type Model as LLMModel } from '@/api/llm';
import type { AutopilotStateSnapshot, AsyncWorkChip } from '@/api/protomia';
import type { CommandItem } from '@/components/commandPalette/CommandPalette.types';
import { CommandPaletteContext } from '@/components/commandPalette/CommandPaletteProvider';
import { LLMDropItem } from '@/types/llmDrop';
import { fileService } from '@/utils/filesService';
import { MarkdownRender } from '@/components/markdownRenderCore';
import useFileStore, { FileInfoExtended } from '@/utils/fileStore';
import { BugOutlined, BulbOutlined, CloseOutlined, FileOutlined, FolderOutlined, QuestionCircleOutlined, ReloadOutlined, RocketOutlined, SafetyOutlined, WarningOutlined } from '@ant-design/icons';
import { App, Button, Modal, Progress, Space, Spin, Tooltip } from 'antd';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, MouseEvent as ReactMouseEvent } from 'react';

import { useTranslation } from 'react-i18next';
// dral 2026-05-17: live chat is now the minimal viewer component
// (browser owns user messages, server ordered-block snapshot
// authoritative, self-healing reconnect watcher, real cancel; pure
// logic unit-tested in chat/components/orderedTranscript.test.ts).
// Legacy AitomiaChat (./chat) kept in-tree, no longer mounted.
// ChatMinimal is loaded lazily so a failed Module Federation fetch
// (remote down / network error) degrades gracefully instead of
// crashing the entire platform page.
// Do NOT add a .catch() here — if the remote is unreachable (404, network
// error, etc.) the rejected Promise must propagate so React throws it to
// the nearest ErrorBoundary, which triggers onError → setChatFailed(true).
const createChatMinimal = () => React.lazy(() => import('aitomistic_chat/Chat'));
import FolderSelector from './folderSelector';
import FeedbackModal from './feedback';
import {
    deriveDelta,
    deriveTarget,
    deriveVerb,
    summariseToolTrace,
} from '@/components/chat/components/toolTrace';
import type { ToolCallLine } from '@/components/chat/components/toolTrace';
import { getRawFileResource, getRawFileResourceInBlob } from '@/api/file';
import { useTourStore } from '@/utils/tour';

/** Catches errors thrown by the ChatMinimal remote so they don't propagate up. */
class ChatErrorBoundary extends React.Component<
    { children: React.ReactNode; onError?: (err?: unknown) => void },
    { hasError: boolean }
> {
    constructor(props: { children: React.ReactNode; onError?: (err?: unknown) => void }) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    override componentDidCatch(err: unknown) {
        this.props.onError?.(err);
    }
    override render() {
        if (this.state.hasError) return null;
        return this.props.children;
    }
}

/** Fires onReady once the lazy ChatMinimal has mounted (Suspense resolved). */
const ChatReadySignal: React.FC<{ onReady: () => void }> = ({ onReady }) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { onReady(); }, []);
    return null;
};
type HistoryItem = {
    type: 'user' | 'bot' | 'system';
    uid?: string;
    content: {
        text?: string,
        node?: React.ReactNode;
        options?: string[];
        files?: Array<{ type: string; name: string; path: string }>;
    },
    footer?: JSX.Element | undefined;
    disabled?: boolean;
    streaming?: boolean;
}

interface SmartProps {
    version?: string;
    dropFiles?: LLMDropItem[]
}

type SelectedFileType = {
    type: "file" | "folder";
    name: string;
    isPined: boolean;
    path: string;
}



const mapBackendRoleToHistoryType = (role?: string): HistoryItem['type'] => {
    if (role === 'human' || role === 'user') {
        return 'user';
    }
    if (role === 'assistant' || role === 'bot') {
        return 'bot';
    }
    return 'system';
};

/**
 * Index of the last still-streaming bot bubble, or -1.
 *
 * dral 2026-05-15 (steering duplication fix): when the user sends a
 * STEER message mid-turn, ``onChat`` appends a ``type:'user'`` bubble
 * to history while the assistant bubble is still streaming. The
 * streaming/final handlers used to look only at
 * ``prev[prev.length-1]`` — which is now the steer message, not the
 * bot bubble — so the next stream chunk spawned a SECOND bot bubble
 * and the whole assistant message was rendered twice (once before the
 * steer, once after). Scanning backwards for the streaming bot bubble
 * keeps a single bubble that grows in place regardless of any
 * user/system items appended after it.
 */
const findLastStreamingBotIdx = (
    arr: ReadonlyArray<HistoryItem>,
): number => {
    for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i]?.type === 'bot' && arr[i]?.streaming) {
            return i;
        }
    }
    return -1;
};

const Chat: React.FC<SmartProps> = ({ dropFiles }) => {
    const { modal, message } = App.useApp();
    const commandPalette = useContext(CommandPaletteContext);

    const [loading, setLoading] = useState(false);
    const baseHome = useFileStore(state => state.baseHome);
    const fileTree = useFileStore(state => state.fileTree);
    const activePanes = useFileStore(state => state.activePanes);

    const activeFolderPath = useFileStore(state => state.activeFolderPath);
    const openFolderOrFile = useFileStore((state) => state.openFolderOrFile);
    const [fixedFileList, setFixedFileList] = useState<Array<SelectedFileType> | undefined>(undefined);
    const [selectedSavePath, setSelectedSavePath] = useState<string>('');
    const aitomicShow = useFileStore(state => state.aitomicShow);
    const setShowAitomia = useFileStore(state => state.setAitomicShow);
    const { t } = useTranslation('common');
    const { t: t_copy } = useTranslation('copy');
    const [aitomiaState, setAitomiaState] = useState<"running" | "pending" | undefined>();

    const current_thread_idRef = useRef<string | null>(null);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [pendingSaveData, setPendingSaveData] = useState<{ content: string; fileName: string } | null>(null);
    const [saveChatLoading, setSaveChatLoading] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const contextCommandDisposeRef = useRef<(() => void) | null>(null);

    const [progressItems, setProgressItems] = useState<any[]>([]);
    // dral 2026-05-15: inline tool-call trace (Claude Code / Copilot
    // style) replaces the floating progressItems panel. The
    // ``progressItems`` state is kept for one release cycle of
    // backwards compat with code paths that haven't migrated yet
    // (history rehydration in particular), but new rendering reads
    // from ``toolTrace`` only.
    const [toolTrace, setToolTrace] = useState<ToolCallLine[]>([]);
    // dral 2026-05-15 (ordered-block model, cross-agent contract
    // post-absence-restore-unification.md §4.2): the reopen/resync GET
    // now returns a server-projected ordered transcript. When present
    // the chat renders DIRECTLY from these blocks (a dumb renderer) —
    // no offset reconstruction, no global toolTrace, no per-turn
    // interleave heuristics, no cross-turn contamination. Live SSE
    // streaming still uses the legacy history/toolTrace path; the
    // block path is reopen/heartbeat-resync only and is cleared at
    // the start of a new user turn so live streaming takes over.
    // ``postAbsence`` is the always-on "while you were away" banner
    // carrier (rendered even when blocks/transcript absent).
    const [blockTranscript, setBlockTranscript] = useState<
        { blocks: any[]; postAbsence: any; transcriptAvailable: boolean } | null
    >(null);
    const [autopilotState, setAutopilotState] = useState<AutopilotStateSnapshot | undefined>(undefined);
    const [asyncWorkChips, setAsyncWorkChips] = useState<AsyncWorkChip[]>([]);
    // dral 2026-05-16 (viewer architecture): highest ordered-block
    // snapshot revision applied. The stream and the GET share this
    // monotonic timeline so a stale in-flight snapshot can never
    // overwrite a fresher one (and vice-versa). A ref (not state) so
    // the SSE handler reads/writes it synchronously without re-render.
    const blocksRevRef = useRef<number>(0);
    // Mirror of userMsgs for synchronous reads inside the SSE handler.
    const userMsgsRef = useRef<{ text: string; files: any[] }[]>([]);
    // Number of persisted user blocks that existed when this session
    // started sending (history baseline). Trailing instant-echo count
    // = userMsgs.length - (currentUserBlocks - baseUserBlocks): show
    // only the just-sent message(s) the authoritative snapshot has not
    // reflected yet. "1 send = exactly 1 user block" makes this
    // count-based bridge drift-free (no ids, no markers, no dedup).
    const baseUserBlocksRef = useRef<number>(0);
    const [baseUserBlocks, setBaseUserBlocks] = useState<number>(0);
    // dral 2026-05-16 (client owns user messages): the ordered list of
    // user messages for this thread, in send order. The browser owns
    // and renders these — appended INSTANTLY on Send (before any
    // await, so zero perceived latency), seeded once from the reopen
    // fetch for history, NEVER cleared mid-conversation. The live
    // stream carries user blocks only as empty positioned markers; the
    // renderer pairs the i-th user marker with userMsgs[i] for the
    // text. No optimistic-echo / dedup dance: user text lives in
    // exactly one place (here), AI/tool blocks in another (the
    // snapshot), so they can never duplicate.
    const [userMsgs, setUserMsgs] = useState<
        { text: string; files: any[] }[]
    >([]);

    const {
        openTour,
    } = useTourStore()

    const [conversationRefreshKey, setConversationRefreshKey] = useState<number>(0);

    const [billingStatus, setBillingStatus] = useState<CurrentMonthTokenBillingStatus | undefined>(undefined);
    const [isProUser, setIsProUser] = useState<boolean>(false);
    const [showUpgradePanel, setShowUpgradePanel] = useState<boolean>(false);
    const [chatLoaded, setChatLoaded] = useState<boolean>(false);
    const [chatFailed, setChatFailed] = useState<boolean>(false);
    // 'load'    = the remote never mounted (a real remoteEntry/module problem)
    // 'backend' = it mounted and later threw; the module is fine, the session is not
    const [chatFailKind, setChatFailKind] = useState<'load' | 'backend'>('load');
    const [chatRetryKey, setChatRetryKey] = useState<number>(0);
    const ChatMinimal = useMemo(() => createChatMinimal(), [chatRetryKey]);
    const [llmModelList, setLlmModelList] = useState<LLMModel[]>([]);
    const getLlmModelList = useCallback(async () => {
        try {
            const res = await llmModelListForUsers();
            if (res?.ok && res?.data) {
                setLlmModelList(res.data);
                return res.data;
            } else {
                console.error('Failed to fetch LLM model list:', res?.message);
                return [];
            }
        } catch (error) {
            console.error('Error fetching LLM model list:', error);
            return [];
        }
    }, []);

    useEffect(() => {
        (async () => {
            const [tokenBillingStatus, proRes] = await Promise.all([
                getCurrentMonthTokenBillingStatus(),
                userIsProtomiaPro(),
                getLlmModelList()
            ]);
            setBillingStatus(tokenBillingStatus);
            setIsProUser(proRes?.ok === true);
        })()

        return () => {
            contextCommandDisposeRef.current?.();
            contextCommandDisposeRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (dropFiles && dropFiles.length > 0) {

            let hasPinedFolder = fixedFileList?.some(item => item.isPined);
            const newFiles = dropFiles.map(item => {
                let isPined = false;
                if (item.type === 'folder' && !hasPinedFolder) {
                    isPined = true;
                    hasPinedFolder = true;
                }
                return {
                    type: item.type,
                    name: item.name,
                    path: item.path,
                    isPined: isPined,
                }
            })

            setFixedFileList((prev) => {
                return [...(prev ?? []), ...newFiles]
            });
        }
    }, [dropFiles])

    const collectContextNodes = useCallback((root?: FileInfoExtended): SelectedFileType[] => {
        if (!root) {
            return [];
        }

        const collected: SelectedFileType[] = [];
        const stack: FileInfoExtended[] = [...(root.children ?? [])];

        while (stack.length > 0) {
            const current = stack.pop();

            if (!current) {
                continue;
            }

            collected.push({
                type: current.isDir ? 'folder' : 'file',
                name: current.name,
                path: current.path,
                isPined: false,
            });

            if (current.children?.length) {
                stack.push(...current.children);
            }
        }

        return collected;
    }, []);

    const onSelectContext = useCallback(() => {
        if (!commandPalette) {
            message.error(t('smart.no_command_palette'));
            return;
        }

        const contextNodes = collectContextNodes(fileTree);

        if (contextNodes.length === 0) {
            message.info(t('smart.no_context_files'));
            return;
        }

        const commands: CommandItem[] = contextNodes.map((item) => ({
            id: `smart.context.${item.path}`,
            title: item.name,
            subtitle: item.path,
            group: item.type === 'folder' ? 'Folders' : 'Files',
            icon: item.type === 'folder' ? <FolderOutlined /> : <FileOutlined />,
            keywords: [item.name, item.path, item.type],
            run: () => {
                setFixedFileList((prev) => {
                    if (prev?.some((existing) => existing.path === item.path)) {
                        return prev;
                    }

                    const next = prev ? [...prev] : [];
                    next.push(item);

                    if (item.type === 'folder') {
                        const folderCount = next.filter((entry) => entry.type === 'folder').length;
                        if (folderCount === 1) {
                            next[next.length - 1] = {
                                ...item,
                                isPined: true,
                            };
                        }
                    }

                    return next;
                });
            },
        }));

        contextCommandDisposeRef.current?.();
        contextCommandDisposeRef.current = commandPalette.registerCommands('smart-context-selector', commands);
        commandPalette.openPalette('');
    }, [collectContextNodes, commandPalette, fileTree]);

    const onReset = useCallback(async (thread_id?: string) => {
        current_thread_idRef.current = null;
        lastEventIdRef.current = null;
        setAitomiaState(undefined);
        setHistory([]);
        setProgressItems([]);
        setToolTrace([]);
        setBlockTranscript(null); blockChatActiveRef.current = false;
        blocksRevRef.current = 0; setUserMsgs([]);
        if (turnReconcileTimerRef.current) { clearTimeout(turnReconcileTimerRef.current); turnReconcileTimerRef.current = null; }
        turnReconcileBusyRef.current = false;
        lastFinalAnswerRef.current = '';
        setAutopilotState(undefined);
        setAsyncWorkChips([]);
        // Close any active SSE so the next message opens fresh.
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setSessionId(undefined);
        const save_result = await ProtomiaApi.reset(thread_id)
        if (!save_result) {
            throw new Error('Network response was not ok');
        }

        current_thread_idRef.current = save_result.thread_id;
    }, []);

    /**
     * Navigation-only thread switch — frontend bookkeeping without a
     * backend ``/api/reset`` call.
     *
     * dral 2026-05-08 leveen-test3 audit (Bug B α):
     * Selecting a conversation in the sidebar is navigation, not a
     * state reset. Pre-fix the chat component called
     * ``onReset(thread_id)`` which posted to ``/api/reset`` and
     * triggered ``protomia.reset()`` server-side. ``protomia.reset()``
     * swapped ``protomia._autopilot_state`` underneath any in-flight
     * JobMonitor for that thread — starving the LLM continuation if
     * a slurm job was running mid-flight (the leveen-test3 symptom:
     * chemistry done, no automatic summary). Backend β fix
     * (commit b0621c5 in aitomic dev) makes ``protomia.reset()``
     * rebuild-aware so even the legacy path is now safe; α removes
     * the unnecessary backend call entirely so navigation is a pure
     * read. The pooled protomia for the new thread is keyed by
     * thread_id server-side and will be created (or reused) on the
     * next chat call — no explicit reset needed.
     */
    const onSwitchThread = useCallback(async (thread_id: string) => {
        current_thread_idRef.current = thread_id;
        lastEventIdRef.current = null;
        setAitomiaState(undefined);
        setHistory([]);
        setProgressItems([]);
        setToolTrace([]);
        setBlockTranscript(null); blockChatActiveRef.current = false;
        blocksRevRef.current = 0; setUserMsgs([]);
        if (turnReconcileTimerRef.current) { clearTimeout(turnReconcileTimerRef.current); turnReconcileTimerRef.current = null; }
        turnReconcileBusyRef.current = false;
        lastFinalAnswerRef.current = '';
        setAutopilotState(undefined);
        setAsyncWorkChips([]);
        // Close the SSE for the previous thread. The next chat
        // call on this new thread will return a fresh sticky
        // session_id and useEffect [sessionId] reopens.
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setSessionId(undefined);
    }, []);


    const stopWorkflow = useCallback(async () => {
        if (!current_thread_idRef.current) {
            return
        }
        const stop_result = await ProtomiaApi.cancel(current_thread_idRef.current || '');

        if (!stop_result) {
            throw new Error('Failed to cancel workflow');
        }

        setAitomiaState(undefined);
        setProgressItems([]);
        setToolTrace([]);
        setBlockTranscript(null); blockChatActiveRef.current = false;
        blocksRevRef.current = 0; setUserMsgs([]);
        if (turnReconcileTimerRef.current) { clearTimeout(turnReconcileTimerRef.current); turnReconcileTimerRef.current = null; }
        turnReconcileBusyRef.current = false;
        lastFinalAnswerRef.current = '';
        setAsyncWorkChips([]);
    }, []);

    const onExport = useCallback(async (aitomiaUrl: string) => {
        window.open(aitomiaUrl + "/api/export", "_blank")
    }, [])

    const onAutopilotCommand = useCallback(async (kind: string, argument?: string) => {
        if (!current_thread_idRef.current) return;
        const result = await ProtomiaApi.autopilotCommand(
            current_thread_idRef.current,
            kind as ProtomiaApi.AutopilotCommandKind,
            argument,
        );
        if (result?.autopilot) {
            setAutopilotState(result.autopilot);
        }
        if (result?.status === 'success' && result?.outcome === 'cancelled') {
            setAsyncWorkChips([]);
            setProgressItems([]);
            setToolTrace([]);
            setBlockTranscript(null); blockChatActiveRef.current = false;
            blocksRevRef.current = 0; setUserMsgs([]);
            if (turnReconcileTimerRef.current) { clearTimeout(turnReconcileTimerRef.current); turnReconcileTimerRef.current = null; }
            turnReconcileBusyRef.current = false;
            lastFinalAnswerRef.current = '';
            setAitomiaState(undefined);
        }
    }, []);

    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [sessionId, setSessionId] = useState<string | undefined>(undefined);
    // dral 2026-05-14 architectural fix (persistent SSE subscription):
    // The frontend used to open a fresh EventSource on every
    // user message (because sessionId changed each turn) AND
    // close it on every final/completed/error event. JobMonitor
    // / watcher continuations published after the close landed
    // on a dead stream — user saw "frozen chat" while backend
    // was working. Fix below:
    //   1. Don't close the EventSource on final/completed/error
    //      (only close on actual disconnect / thread switch).
    //   2. Don't clear sessionId on final — the backend's
    //      ``thread_sessions`` map returns the SAME session_id
    //      on the next chat call (sticky-per-thread), so
    //      useEffect dep [sessionId] sees no change → no reopen.
    //
    // The earlier attempt switched to a thread-keyed URL
    // (``/agent/sse/thread/{thread_id}``); reverted because the
    // Go dev_backend's gorilla router only matches
    // ``/agent/sse/{sessionID}`` (single segment) and adding
    // the matching Go route is a separate deploy. The
    // session_id URL + sticky-per-thread semantics give the
    // same persistent-subscription behaviour without needing
    // any Go-backend or NATS-subject changes.
    const eventSourceRef = useRef<EventSource | null>(null);
    const reconnectCountRef = useRef<number>(0);
    const lastEventIdRef = useRef<number | null>(null);
    const isSyncingConversationRef = useRef<boolean>(false);
    const pendingSSEMessagesRef = useRef<any[]>([]);
    // dral 2026-05-15 (#6 interleave): length of the assistant's
    // accumulated prose at the moment the most recent SSE event was
    // processed. Stamped onto each tool line's ``textOffset`` when a
    // call STARTS so the renderer can weave commands into the message
    // stream at the right point. Reset to 0 at the start of each user
    // turn (onChat).
    const assistantTextLenRef = useRef<number>(0);
    // dral 2026-05-15: true while a reopened (block-transcript) chat
    // is active. After a live turn completes in such a chat we
    // auto-resync so the new turn folds into the persisted block
    // transcript automatically — otherwise the user only saw the
    // refreshed full conversation "after reload".
    const blockChatActiveRef = useRef<boolean>(false);
    // dral 2026-05-15: wall-clock of the last SSE event we received.
    // The always-on heartbeat (below) uses this to detect a silent
    // backend restart and auto-rehydrate from the DB WITHOUT the user
    // reopening the chat ("it should have a monitor always watching
    // and rehydrating if there was a restart").
    const lastSseActivityRef = useRef<number>(Date.now());
    const maxReconnectAttempts = 3;
    // dral 2026-05-16 (Issue C — end-of-turn reconcile): timer +
    // dedupe guard for the single, delayed, GUARDED post-final
    // resync that swaps the messy live render for the SAME clean
    // persisted block render the user gets on reload (one answer,
    // consistent summary, no echoed-prompt bubble). Distinct from the
    // 2026-05-15 broken auto-resync (which fired MID-stream and
    // fragmented the final) — this only fires AFTER the turn truly
    // ended + a settle delay, and only adopts when the finished
    // turn's assistant answer is actually persisted (else it leaves
    // the live render untouched and retries once).
    const turnReconcileTimerRef = useRef<any>(null);
    const turnReconcileBusyRef = useRef<boolean>(false);
    // dral 2026-05-16 (Issue C, proper): the FE already receives the
    // COMPLETE final answer in the SSE 'final' event. The persisted
    // ordered-block transcript, however, is written progressively —
    // reading it too early yields a still-mid-write (fragmented /
    // short) assistant answer, which is exactly the flicker +
    // fragmented reply seen right before the clean view. So the
    // post-final reconcile adopts the persisted render ONLY once its
    // assembled assistant text is at least as complete as this known
    // final answer — deterministic, no heuristics, no fragment.
    const lastFinalAnswerRef = useRef<string>('');
    // dral 2026-05-16 (viewer): the thread the live EventSource is
    // currently subscribed to. Lets initializeSSE be idempotent — a
    // setSessionId-triggered re-init for the SAME thread is a no-op
    // (so subscribe-before-POST is not torn down + reopened, which
    // would briefly drop the subscription and miss early events).
    const sseThreadRef = useRef<string | null>(null);

    const initializeSSE = async () => {
        const threadId = current_thread_idRef.current;
        if (!threadId) {
            // Initial mount can happen before a thread is created.
            // Silently skip and let onChat/onSwitchThread trigger init later.
            return;
        }
        console.log("initialize SSE for thread", threadId);
        // Already subscribed to this exact thread on a live socket →
        // keep it (do not close+reopen).
        if (
            eventSourceRef.current
            && sseThreadRef.current === threadId
            && eventSourceRef.current.readyState !== EventSource.CLOSED
        ) {
            return;
        }
        sseThreadRef.current = threadId;

        if (!import.meta.env.VITE_API_URL || import.meta.env.VITE_API_URL === '') {
            console.error("VITE_API_URL is not set, cannot start streaming");
            return;
        }

        // 关闭现有连接 (close any existing connection — happens
        // when switching between chat threads; within a single
        // thread the useEffect dep array keeps the same EventSource
        // alive across user messages)
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        // dral 2026-05-14 architectural fix (subscribe-by-thread-id):
        // SSE URL keyed on the stable thread_id, not the per-
        // message session_id. The full stack supports this:
        //
        //   Frontend EventSource → /api/agent/sse/thread/{thread_id}
        //   Go dev_backend handler ``AgentSSEThreadHandler`` subscribes
        //     to NATS subject ``aitomia.thread.{thread_id}``
        //   Protomia ``MessagePublisher.publish`` emits to BOTH
        //     ``aitomia.chat.{session_id}`` (legacy) and
        //     ``aitomia.thread.{thread_id}`` (new) so all subscribers
        //     stay served during the migration window
        //
        // Net effect: every event for this conversation reaches
        // this EventSource regardless of which protomia code path
        // emitted it. Eliminates the "publish to a synthetic or
        // stale session_id" class of bugs (every recent UX patch
        // worked around the same root cause).
        const sseUrl = `${import.meta.env.VITE_API_URL}/agent/sse/thread/${threadId}`;

        eventSourceRef.current = new EventSource(sseUrl, {
            withCredentials: false,
        });

        eventSourceRef.current.onopen = () => {
            console.log("SSE connection opened");
            if (reconnectCountRef.current > 0) {
                syncConversationMessages();
            }
            // 连接成功，重置重连计数
            reconnectCountRef.current = 0;
        }

        eventSourceRef.current.onmessage = (event) => {
            lastSseActivityRef.current = Date.now();
            const data = JSON.parse(event.data);
            console.log("SSE event received:", data);
            console.log("message:", data?.message);

            // Buffer messages while a DB sync is in progress to avoid race conditions
            // where setHistory(dbMessages) would overwrite concurrently appended SSE messages
            if (isSyncingConversationRef.current) {
                pendingSSEMessagesRef.current.push(data);
                return;
            }

            const incomingEventId = Number(data?.event_id);
            const hasIncomingEventId = Number.isFinite(incomingEventId);
            const lastEventId = lastEventIdRef.current;

            if (hasIncomingEventId && typeof lastEventId === 'number' && incomingEventId > lastEventId + 1) {
                console.warn(`Detected missing SSE events. last=${lastEventId}, current=${incomingEventId}`);
                syncConversationMessages();
                return;
            }

            if (hasIncomingEventId && typeof lastEventId === 'number' && incomingEventId <= lastEventId) {
                return;
            }

            if (hasIncomingEventId) {
                lastEventIdRef.current = incomingEventId;
            }

            if (data?.thread_id && current_thread_idRef.current && data?.thread_id !== current_thread_idRef.current) {
                return;
            }

            // ── Autopilot / conductor snapshot (every chat event) ─────────
            // dral 2026-05-15: the backend emits the live plan under
            // ``data.conductor`` (version/mode/status/goal/steering/
            // todo[…]). The frontend previously only read
            // ``data.autopilot`` — which the conductor path never
            // sends — so the todo checklist + pause status never
            // surfaced (user: "I do not see any todo list"; "pause
            // button does not work"). Map conductor → the panel's
            // AutopilotStateSnapshot shape; keep data.autopilot as a
            // fallback for any legacy autopilot path.
            if (data?.autopilot) {
                setAutopilotState(data.autopilot);
            } else if (data?.conductor) {
                const c = data.conductor;
                const todoRaw: any[] = Array.isArray(c?.todo) ? c.todo : [];
                const isDone = (s: string) =>
                    s === 'completed' || s === 'done' || s === 'verified' || s === 'skipped';
                const completed = todoRaw.filter((s) => isDone(String(s?.status))).length;
                const running = todoRaw.find((s) => String(s?.status) === 'running');
                setAutopilotState({
                    status: c?.status,
                    todo: todoRaw.map((s) => ({
                        id: String(s?.id ?? ''),
                        title: String(s?.title ?? ''),
                        status: String(s?.status ?? 'pending'),
                    })),
                    current_step_id: running?.id,
                    current_step_title: running?.title,
                    completed_count: completed,
                    total_count: todoRaw.length,
                });
            }

            // ── Viewer architecture (dral 2026-05-16) ─────────────────────
            // The browser is a PURE VIEWER of the server-authoritative
            // ordered-block snapshot. Every backend event also carries
            // the full ordered-block list tagged with a monotonic
            // ``blocks_rev``. We keep the highest-rev snapshot and
            // render ONLY from it — no live history fold, no toolTrace
            // reconstruction, no timer reconcile, no dual
            // representation. Multi-step / continuation turns interleave
            // correctly by construction because ``position`` is
            // authoritative on the wire and on screen.
            if (data?.event_type === 'blocks') {
                const rev = Number(data?.blocks_rev);
                if (Number.isFinite(rev) && rev > blocksRevRef.current) {
                    blocksRevRef.current = rev;
                    const blks: any[] = Array.isArray(data?.blocks) ? data.blocks : [];
                    setBlockTranscript((prev) => ({
                        blocks: blks,
                        // post_absence comes from the GET path; keep the
                        // last known banner across live snapshots.
                        postAbsence: prev?.postAbsence ?? null,
                        transcriptAvailable: blks.length > 0,
                    }));
                    blockChatActiveRef.current = blks.length > 0;
                    // Capture the history baseline once, before this
                    // session has sent anything: how many user blocks
                    // already existed. The count-based trailing echo is
                    // measured against NEW user blocks since then.
                    if (userMsgsRef.current.length === 0) {
                        const u = blks.filter(
                            (b: any) => b?.type === 'text' && b?.role === 'user',
                        ).length;
                        baseUserBlocksRef.current = u;
                        setBaseUserBlocks(u);
                    }
                }
                return;
            }

            // ── Structured progress events (progress_kind present) ────────
            // These use the new envelope format and may NOT have data.message.
            if (data?.event_type === 'progress' && data?.progress_kind) {
                const kind: string = data.progress_kind;
                if (kind === 'async_work_chip_update') {
                    setAsyncWorkChips((prev) => {
                        const key = `${data.handle_source}:${data.handle_id}`;
                        const idx = prev.findIndex(
                            (c) => `${c.handle_source}:${c.handle_id}` === key,
                        );
                        const chip: AsyncWorkChip = {
                            handle_source: data.handle_source,
                            handle_id: data.handle_id,
                            sacct_status: data.sacct_status,
                            queue_position: data.queue_position,
                            runtime_seconds: data.runtime_seconds,
                            bundle_dir: data.bundle_dir,
                            recent_log_tail: data.recent_log_tail,
                        };
                        if (idx >= 0) {
                            const next = [...prev];
                            next[idx] = chip;
                            return next;
                        }
                        return [...prev, chip];
                    });
                } else if (kind === 'async_work_poll_tick') {
                    setAsyncWorkChips((prev) => {
                        const key = `${data.handle_source}:${data.handle_id}`;
                        const idx = prev.findIndex(
                            (c) => `${c.handle_source}:${c.handle_id}` === key,
                        );
                        if (idx >= 0) {
                            const next = [...prev];
                            next[idx] = {
                                ...next[idx],
                                sacct_status: data.last_known_status ?? next[idx].sacct_status,
                                runtime_seconds: data.runtime_seconds ?? next[idx].runtime_seconds,
                            };
                            return next;
                        }
                        return [
                            ...prev,
                            {
                                handle_source: data.handle_source,
                                handle_id: data.handle_id,
                                sacct_status: data.last_known_status,
                                runtime_seconds: data.runtime_seconds,
                            },
                        ];
                    });
                }
                // Other future progress_kind values (tool_dispatch, llm_call,
                // autopilot_step_changed, etc.) — no-op until backend wires them.
                // Skip the message-handling block below.
            } else if (data?.event_type === 'title_update' && data?.title) {
                // Title changed → refresh the conversation list only.
                // The transcript itself is driven solely by 'blocks'
                // snapshots (handled + returned above).
                setConversationRefreshKey((k) => k + 1);
            } else if (false && data?.message) {
                // dral 2026-05-16 VIEWER ARCHITECTURE: the entire legacy
                // message/final/options transcript-mutation path below
                // is permanently disabled. The server-authoritative
                // ordered-block snapshot ('blocks' event, handled and
                // returned at the top of onmessage) is the single
                // source of truth. Kept (dead-coded) only for diff
                // clarity / quick revert during bring-up; delete once
                // the viewer model is proven on preprod.
                if (data?.event_type === 'title_update' && data?.title) {
                    setConversationRefreshKey((k) => k + 1);
                }
                if (data?.event_type === 'final' || data?.status === 'completed' || data?.status === 'error') {
                    setProgressItems([]);
                    // dral 2026-05-15: do NOT clear ``toolTrace`` here.
                    // Claude-Code parity — the tool-call / thinking
                    // lines must stay visible after the final message
                    // lands, not vanish the instant the turn ends. The
                    // trace is cleared at the START of the next user
                    // turn instead (see onChat → setToolTrace([])), and
                    // rehydrated from persisted tool_call rows on
                    // reload. Pre-fix this line wiped the whole trace
                    // on every final → the user saw the activity
                    // disappear as soon as (or before) the answer
                    // arrived.
                    // dral 2026-05-14 Fix C: on final/completed/error,
                    // RENDER the data.message text. Pre-fix this branch
                    // only marked the streaming bubble as done without
                    // updating its text content → live UI showed
                    // whatever was last streamed (or empty), then reload
                    // pulled the DB-persisted final message which was
                    // different. Now: if the streaming bubble's last-
                    // rendered text matches data.message (LLM finished
                    // naturally), just mark it done. If they DIFFER
                    // (e.g. cadence-fuse abort with a different
                    // message), append a new bubble for the abort so
                    // both messages persist visually.
                    // Issue C (proper): remember the COMPLETE final
                    // answer ONLY on the TRUE turn-final event. This
                    // branch also fires on sub-step `status==='completed'`
                    // (a tool/step finishing) — those are NOT the turn
                    // end and must NOT arm the reconcile, otherwise it
                    // ran mid-turn, found no captured final, degraded
                    // to "adopt any persisted text", and clobbered the
                    // live render on every step → the persistent
                    // flicker + user-message shift.
                    if (data?.event_type === 'final') {
                        lastFinalAnswerRef.current = (data?.message as string) || '';
                    }
                    setHistory(prev => {
                        const finalText = (data?.message as string) || '';
                        // Scan backwards: a steer message may sit AFTER
                        // the streaming bot bubble (see
                        // findLastStreamingBotIdx).
                        const idx = findLastStreamingBotIdx(prev);
                        const streamingBot = idx >= 0 ? prev[idx] : null;
                        const lastText = streamingBot ? (streamingBot.content?.text || '') : '';
                        // dral 2026-05-16 (live duplicate-final fix):
                        // since Bug-3 coalescing drops intermediate
                        // cumulative frames, the LAST streamed frame the
                        // FE holds is usually a PREFIX of the complete
                        // `final` text — so lastText !== finalText even
                        // on a perfectly normal completion. The old
                        // code then appended a SECOND bubble (intended
                        // only for genuine cadence-fuse aborts),
                        // producing the transient duplicated answer the
                        // user saw (until reconcile collapsed it).
                        // Treat "one is a prefix of the other" (same
                        // answer, just truncated by coalescing) as
                        // natural completion: update the single bubble
                        // to the COMPLETE final text and mark done — no
                        // duplicate, and the live bubble is immediately
                        // correct (no reliance on reconcile for the
                        // common case). Only a GENUINELY divergent final
                        // (neither a prefix of the other → a real abort
                        // with replacement text) keeps both bubbles.
                        const _a = (lastText || '').trim();
                        const _b = (finalText || '').trim();
                        const naturalCompletion =
                            !_a || !_b || _b.startsWith(_a) || _a.startsWith(_b);
                        if (streamingBot && (lastText === finalText
                            || finalText === '' || naturalCompletion)) {
                            // Single bubble: show the complete final
                            // text (authoritative) unless final was
                            // empty (keep what streamed). Mark done.
                            const updated = [...prev];
                            updated[idx] = {
                                ...streamingBot,
                                streaming: false,
                                content: {
                                    ...streamingBot.content,
                                    text: finalText || lastText,
                                    files: data?.files ?? streamingBot.content?.files,
                                },
                            };
                            return updated;
                        }
                        if (streamingBot) {
                            // GENUINELY divergent final (true abort —
                            // not a continuation of the streamed text).
                            // Finalize the streamed bubble preserving
                            // its text, then append a NEW bubble for
                            // the divergent final so both are visible.
                            const updated = [...prev];
                            updated[idx] = { ...streamingBot, streaming: false };
                            updated.push({
                                type: 'bot' as const,
                                streaming: false,
                                content: {
                                    text: finalText,
                                    files: data?.files,
                                },
                            });
                            return updated;
                        }
                        // No streaming bubble — just append the final
                        // message as a new bot bubble.
                        return [...prev, {
                            type: 'bot' as const,
                            streaming: false,
                            content: {
                                text: finalText,
                                files: data?.files,
                            },
                        }];
                    });
                }
                // Old-style progress (has data.message AND data.progress)
                if (data?.event_type === 'progress' && data?.progress) {
                    setProgressItems((prev) => {
                        const next = [...prev, data.progress];
                        return next;
                    });
                }
                else if (data?.event_type !== 'title_update' && data?.event_type !== 'final') {
                    // #6 interleave: record how much assistant prose
                    // has streamed so the NEXT tool dispatch is woven
                    // in at this point. (The toolTrace fold above ran
                    // earlier in this handler with the PRE-update
                    // length — exactly "text before this tool".)
                    assistantTextLenRef.current = String(data?.message || '').length;
                    setHistory(prevHistory => {
                        const idx = findLastStreamingBotIdx(prevHistory);
                        if (idx >= 0) {
                            // Update the streaming bubble IN PLACE (even
                            // if a steer message was appended after it).
                            const prevItem = prevHistory[idx];
                            const updated = [...prevHistory];
                            updated[idx] = {
                                ...prevItem,
                                content: {
                                    ...prevItem.content,
                                    text: data?.message || '',
                                    files: data?.files ?? prevItem.content.files,
                                },
                            };
                            return updated;
                        }
                        // Start a new streaming bot bubble
                        return [...prevHistory, {
                            type: 'bot' as const,
                            streaming: true,
                            content: {
                                text: data?.message,
                                files: data?.files,
                            },
                        }];
                    });
                }

                if (data?.options && Array.isArray(data.options) && data.options.length > 0) {
                    setHistory(prevHistory => [...prevHistory, {
                        type: 'system',
                        content: {
                            options: data?.options,
                        },
                    }]);
                }

            }

            if (data?.status) {
                setAitomiaState(data.status);
            }

            // dral 2026-05-14 architectural fix (subscribe-by-
            // thread-id): on final/completed/error, just mark the
            // current bot bubble as done — DO NOT close the
            // EventSource. The subscription stays open so the
            // next turn's events (JobMonitor continuations,
            // watcher revivals, post-failure remediation) arrive
            // on the same connection. Pre-fix the close-on-final
            // behaviour was the root cause of "chat looks frozen
            // but backend is working" — when a JobMonitor
            // continuation fired AFTER a fuse-abort final event,
            // the new turn's events landed on a closed
            // EventSource and the user saw nothing.
            if (data.status === 'end' || data.status === 'completed' || data.status === 'error') {
                // Viewer architecture: the transcript is authoritative
                // via the 'blocks' snapshot (the terminal event already
                // published one carrying the full final text as the
                // synthetic tail, so the answer is on screen before the
                // store finishes persisting — this is what fixes
                // "final only appears after reload"). Here we only
                // clear transient SIDE-PANEL state. NO history mutation,
                // NO scheduleTurnEndReconcile — there is no second
                // representation to reconcile anymore. The EventSource
                // and sticky session stay open so continuation turns
                // (JobMonitor / watcher revivals) keep streaming
                // snapshots onto the same connection.
                // Mark the last streaming bot bubble as done
                setHistory(prev => {
                    const last = prev[prev.length - 1];
                    if (last && last.type === 'bot' && last.streaming) {
                        const updated = [...prev];
                        updated[updated.length - 1] = { ...last, streaming: false };
                        return updated;
                    }
                    return prev;
                });

                if (data.status === "error") {

                    setHistory(prev => {

                        return [...prev, {
                            type: 'bot' as const,
                            streaming: true,
                            content: {
                                text: data?.message,
                                files: data?.files,
                            },
                        }];
                    })
                }

                // Clear per-turn progress items / chips. Do NOT
                // clear sessionId — it's STICKY per thread (the
                // backend's ``thread_sessions`` map returns the
                // same value on the next chat call), so keeping
                // it set means the useEffect dep [sessionId] sees
                // no change → EventSource stays open → JobMonitor
                // continuations on NATS subject
                // ``aitomia.chat.{sessionId}`` reach this same
                // EventSource. Pre-fix this cleared sessionId,
                // which reopened the EventSource per message and
                // missed continuations.
                setProgressItems([]);
                setAsyncWorkChips([]);
            }
        };

        // 'complete': also keep sessionId set — see comment
        // above. The EventSource and sticky session both persist
        // for the lifetime of the chat tab on this thread.
        eventSourceRef.current.addEventListener('complete', () => {
            console.log("SSE turn complete (subscription stays open for thread)");
        });

        eventSourceRef.current.addEventListener('error', (err) => {
            console.error("SSE connection error:", err);

            // 检查连接状态
            if (eventSourceRef.current?.readyState === EventSource.CLOSED) {
                console.log("SSE connection closed by server");
                eventSourceRef.current = null;
                setSessionId(undefined);
                setAitomiaState(undefined);
            } else if (eventSourceRef.current?.readyState === EventSource.CONNECTING) {
                reconnectCountRef.current += 1;
                console.log(`SSE reconnecting... (Attempt ${reconnectCountRef.current}/${maxReconnectAttempts})`);
                syncConversationMessages();

                // 如果重连次数超过限制，关闭连接
                if (reconnectCountRef.current >= maxReconnectAttempts) {
                    console.error("Max reconnection attempts reached. Closing SSE connection.");
                    eventSourceRef.current?.close();
                    eventSourceRef.current = null;
                    setSessionId(undefined);
                    setAitomiaState(undefined);
                }
            }
        });
    }

    // dral 2026-05-14 architectural fix (subscribe-by-thread-id):
    // dep array is keyed on res_chat.thread_id (via sessionId
    // change as proxy — sessionId is sticky per thread, so it
    // only differs when thread changes). useEffect only re-fires
    // on actual thread change → one persistent EventSource per
    // chat tab. The URL itself uses current_thread_idRef.current
    // (synced to res_chat.thread_id in onChat).
    useEffect(() => {
        if (!current_thread_idRef.current) {
            return;
        }
        initializeSSE();
    }, [sessionId]);

    // dral 2026-05-15: ALWAYS-ON rehydration monitor.
    //
    // User: "the point is to rehydrate automatically, not on
    // reopening chat, but always, especially after restart of the
    // service. It should have a monitor always watching and
    // rehydrating if there was a restart."
    //
    // The native EventSource auto-reconnect + the error-handler
    // resync only fire on a detected drop and give up after
    // ``maxReconnectAttempts`` (then the chat sat frozen until the
    // user manually reopened it). This interval runs for the whole
    // lifetime of the chat tab and, independently of the
    // EventSource's own retry budget:
    //   * if a thread is open and the SSE socket is gone/closed →
    //     reopen it (a backend restart silently kills the socket);
    //   * if a turn is running but no SSE event has arrived for a
    //     while (backend restarted mid-turn and finished the work
    //     while we were disconnected) → pull the authoritative state
    //     from the DB (syncConversationMessages = the rehydrate).
    // Cheap (12 s tick, guarded by isSyncingConversationRef).
    useEffect(() => {
        const HEARTBEAT_MS = 12_000;
        const STALE_MS = 25_000;
        const id = setInterval(() => {
            const threadId = current_thread_idRef.current;
            if (!threadId || isSyncingConversationRef.current) return;

            const es = eventSourceRef.current;
            const socketDead = !es || es.readyState === EventSource.CLOSED;
            const running = aitomiaState === 'running';
            const stale =
                running && (Date.now() - lastSseActivityRef.current) > STALE_MS;

            if (socketDead || stale) {
                // Reset the native retry budget so a later genuine
                // drop still gets its reconnect attempts.
                reconnectCountRef.current = 0;
                // Pull the authoritative conversation state from the
                // DB — this is the actual auto-rehydrate. Safe: it's
                // idempotent and guarded by isSyncingConversationRef.
                syncConversationMessages();
                // If the live socket is gone, reopen it so subsequent
                // turns stream again (a restart kills it silently).
                if (socketDead) {
                    try { initializeSSE(); } catch { /* best-effort */ }
                }
            }
        }, HEARTBEAT_MS);
        return () => clearInterval(id);
    }, [aitomiaState]);

    const onChat = useCallback(async (request: ChatRequest) => {
        // dral 2026-05-16: INSTANT feedback FIRST — before ANY network
        // await. The user's own message + the running state must
        // appear with zero perceived latency. Previously the billing
        // round-trip ran first and gated everything → the chat looked
        // frozen for as long as that call took (~40s observed). The
        // user message lives in our own outgoing list; the snapshot is
        // authoritative and will render the same message from its
        // persisted user block within the first snapshot — the
        // count-based trailing echo just bridges that sub-second gap.
        const _outgoing = {
            text: request.message,
            files: Array.isArray(fixedFileList) ? fixedFileList : [],
        };
        setUserMsgs(prev => {
            const next = [...prev, _outgoing];
            userMsgsRef.current = next;
            return next;
        });
        setAitomiaState("running");
        setProgressItems([]);
        setToolTrace([]);

        // dral 2026-05-15: do NOT clear blockTranscript here. On a
        // reopened (block-path) chat the prior conversation lives in
        // blockTranscript and history is []. Clearing it on send made
        // the whole transcript vanish — looked like a fresh chat —
        // until the turn finished and the resync restored it ("the
        // whole thing jumps"). Instead the prior block transcript
        // stays rendered and the new live turn streams BELOW it via
        // the legacy history/toolTrace path; the end-of-turn resync
        // then folds the new turn into a refreshed blockTranscript
        // seamlessly (Chat stacks block-lead + live-tail).
        assistantTextLenRef.current = 0; // #6: reset interleave offset baseline
        lastEventIdRef.current = null;
        // Issue C: cancel any pending post-final reconcile from the
        // PREVIOUS turn — it must never fire during this new live
        // turn (would clobber the in-flight render).
        if (turnReconcileTimerRef.current) {
            clearTimeout(turnReconcileTimerRef.current);
            turnReconcileTimerRef.current = null;
        }
        turnReconcileBusyRef.current = false;
        // (The user's message was already appended to userMsgs at the
        // very top of onChat — instant, before any await.)
        // dral 2026-05-16 (viewer architecture): the prior-turn
        // reconcile flush is DELETED. There is no second representation
        // to fold — the server keeps emitting the authoritative
        // ordered-block snapshot for the whole thread, so the prior
        // turn is already correctly rendered and the new turn's blocks
        // simply extend it in position order. Nothing to flush, no
        // completeness gate, no GET-before-send latency.


        // dral 2026-05-16 (viewer, subscribe-before-POST): for a NEW
        // chat, generate the thread_id CLIENT-SIDE and open the thread
        // SSE BEFORE the POST. Previously the FE only learned the
        // thread_id from the POST response and subscribed after — so a
        // slow/failed POST left initializeSSE running with thread=null
        // ("No thread ID available for SSE connection", observed
        // looping) and every streamed block/ack was missed. The
        // backend honours a client-provided Thread-ID for a new
        // thread (same contract the self-test harness relies on), so
        // subscribing first guarantees we catch the +6s ack and all
        // ordered-block snapshots from event #1.
        if (!current_thread_idRef.current) {
            try {
                current_thread_idRef.current =
                    (crypto as any)?.randomUUID?.()
                    || `t-${Date.now()}-${Math.random().toString(16).slice(2)}`;
            } catch {
                current_thread_idRef.current =
                    `t-${Date.now()}-${Math.random().toString(16).slice(2)}`;
            }
            try { await initializeSSE(); } catch { /* best-effort */ }
        }

        try {
            const res_chat = await ProtomiaApi.chat(
                {
                    messages: { role: 'human', content: request.message },
                    working_directory: request.working_directory,
                    client_action_id: request.client_action_id || null,
                },
                current_thread_idRef.current || ''
            )

            if (!res_chat) {
                message.error(t('smart.network_error'));
                setAitomiaState(undefined);
                return;
            }

            if (res_chat?.thread_id) {
                current_thread_idRef.current = res_chat.thread_id;
            }
            // dral 2026-05-14 architectural fix (persistent SSE):
            // sessionId is sticky-per-thread on the backend
            // (``thread_sessions`` map). Setting it triggers the
            // useEffect dep [sessionId] equality check — for
            // subsequent messages on the same thread the value
            // is the same so no EventSource reopen happens.
            setSessionId(res_chat.session_id);
            setFixedFileList(undefined);
        } catch (error) {
            console.error('Chat request failed:', error);
            message.error(t('smart.chat_failed') + (error as Error).message);
            setAitomiaState(undefined);
        }
    }, [fixedFileList]);

    const onSaveCurrentWorkingPath = useCallback(async (files: SelectedFileType[]) => {
        await ProtomiaApi.save({ save_path: files });
    }, [])

    const getConversationList = useCallback(async () => {
        const conversation_result = await ProtomiaApi.getConversationList();
        if (!conversation_result) {
            throw new Error('Network response was not ok');
        }

        if (!conversation_result?.conversations) {
            message.error(t('smart.fetch_conversations_failed'));
            return [];
        }
        if (aitomiaState !== 'running') {
            setAitomiaState(undefined);
        }
        return conversation_result.conversations;
    }, [aitomiaState])

    const getConversationMessages = useCallback(async (thread_id: string, hideLoading?: boolean) => {
        if (!hideLoading) {
            setLoading(true);
        }
        try {
            const conversation_message_result = await ProtomiaApi.getConversationMessages(thread_id);
            if (!conversation_message_result) {
                throw new Error('Network response was not ok');
            }

            const messages = Array.isArray(conversation_message_result?.messages) ? conversation_message_result.messages : [];

            // Ordered-block transcript (server-projected, schema v1) +
            // the always-on post_absence banner carrier. Captured
            // unconditionally; when blocks[] is non-empty the chat
            // renders from it (clean dumb path) and the legacy
            // history/toolTrace are forced empty so they don't
            // double-render.
            const respBlocks: any[] = Array.isArray(conversation_message_result?.blocks)
                ? conversation_message_result.blocks : [];
            const respPostAbsence = conversation_message_result?.post_absence ?? null;
            const respTranscriptAvailable = !!conversation_message_result?.transcript_available;
            setBlockTranscript({
                blocks: respBlocks,
                postAbsence: respPostAbsence,
                transcriptAvailable: respTranscriptAvailable,
            });
            // History baseline (count-based echo): capture the number
            // of pre-existing user blocks the first time we load a
            // transcript before this session has sent anything. The
            // trailing instant-echo shows only this-session sends not
            // yet reflected as NEW user blocks beyond this baseline.
            if (userMsgsRef.current.length === 0) {
                const u = respBlocks.filter(
                    (b: any) => b?.type === 'text' && b?.role === 'user',
                ).length;
                baseUserBlocksRef.current = u;
                setBaseUserBlocks(u);
            }
            // Viewer architecture: the GET and the live stream share
            // ONE monotonic rev timeline per thread. This GET reflects
            // the thread's current authoritative rev; adopt it as the
            // floor so a late/stale in-flight stream snapshot can't
            // regress the view, while a genuinely newer snapshot
            // (rev greater) still applies. (Thread switch routes
            // through here for the new thread, so the ref re-bases
            // to that thread's rev automatically.)
            blocksRevRef.current = Number(
                (conversation_message_result as any)?.blocks_rev,
            ) || 0;
            blockChatActiveRef.current = respBlocks.length > 0 || !!respPostAbsence;
            const useBlockPath = respBlocks.length > 0;

            if (!messages.length && !useBlockPath) {
                // No transcript — but a post_absence mirror may still
                // exist (non-postgres degraded 200): show the banner,
                // don't error.
                if (!respPostAbsence) {
                    message.error("No messages in this conversation");
                }
                setHistory([]);
                setProgressItems([]);
                setToolTrace([]);
                lastEventIdRef.current = null;
                setLoading(false);
                return [];
            }

            const maxEventId = messages.reduce((maxId: number, msg: any) => {
                const current = Number(msg?.event_id);
                if (!Number.isFinite(current)) {
                    return maxId;
                }
                return Math.max(maxId, current);
            }, -1);
            if (maxEventId >= 0) {
                lastEventIdRef.current = maxEventId;
            }

            const latestStatusMessage = [...messages].reverse().find((msg: any) => !!msg?.status);
            setAitomiaState(latestStatusMessage?.status);

            // If the conversation has already ended, close the SSE connection immediately
            if (latestStatusMessage?.status && ['end', 'completed', 'error'].includes(latestStatusMessage.status)) {
                eventSourceRef.current?.close();
                eventSourceRef.current = null;
                setSessionId(undefined);
            }

            const progressList = messages
                .filter((msg: any) => msg?.event_type === 'progress' || msg?.type === 'progress')
                .map((msg: any) => ({
                    id: String(msg?.id ?? `${msg?.created_at || Date.now()}`),
                    stage: msg?.stage || 'progress',
                    message: msg?.message || msg?.content || '',
                    status: msg?.status,
                    detail: msg?.detail ?? null,
                    percent: msg?.percent ?? null,
                    timestamp: msg?.created_at ?? null,
                }));

            // Phase F (dral 2026-05-15): re-hydrate the inline
            // tool-call trace from persisted ``type='tool_call'`` rows.
            // The backend bridge in protomia_server.py stores every
            // ``progress_kind=tool_dispatch_finished`` event as a
            // ``role='tool', type='tool_call'`` row with the
            // ToolCallLine-shaped JSON in ``content``. Reload now
            // gives the same inline trace the user saw live.
            const rehydratedTrace: ToolCallLine[] = [];
            for (const msg of messages) {
                if (msg?.type !== 'tool_call') continue;
                let parsed: any = null;
                try {
                    parsed = typeof msg.content === 'string'
                        ? JSON.parse(msg.content) : msg.content;
                } catch {
                    continue;
                }
                if (!parsed?.tool_name) continue;
                const toolName: string = parsed.tool_name;
                const skill: string | undefined = parsed.skill_name || undefined;
                const argsFull: string = parsed.arguments_summary || '';
                const outputFull: string | undefined = parsed.output_summary || undefined;
                const status = parsed.status === 'error' ? 'error' : 'success';
                const id = `${skill || 'orchestrator'}:${toolName}:${thread_id}:${argsFull.slice(0, 32)}:${msg.id}`;
                rehydratedTrace.push({
                    id,
                    verb: 'Read', // overwritten by the fold logic if events restream
                    target: '',
                    status: status as 'success' | 'error',
                    durationS: parsed.duration_seconds,
                    argsFull,
                    outputFull,
                    delta: undefined,
                    skill,
                    toolName,
                });
            }
            // Apply verb/target/delta derivation so re-hydrated
            // rows render the same way as live ones.
            for (const line of rehydratedTrace) {
                line.verb = deriveVerb(line.toolName, line.argsFull);
                line.target = deriveTarget(line.toolName, line.argsFull);
                line.delta = deriveDelta(line.toolName, line.outputFull);
            }
            // dral 2026-05-15 (#6 robustness): a DB re-sync fires at
            // end-of-turn / on SSE reconnect. If the backend did not
            // persist ``type='tool_call'`` rows (e.g. a branch where
            // Phase-F persistence differs) ``rehydratedTrace`` is
            // empty — and blindly ``setToolTrace([])`` here WIPED the
            // live, correctly-folded trace, so every interleaved
            // command vanished from the finished turn ("in the end
            // state it lost all the run commands"). Only replace the
            // trace when the DB actually returned rows; otherwise keep
            // whatever the live SSE fold built. A genuine switch to a
            // tool-less conversation already cleared the trace via
            // onSwitchThread/onReset, so ``prev`` is [] there anyway.
            // Never DOWNGRADE: the post-turn / reconnect re-sync can
            // return a partial set (e.g. only the top-level
            // skill-tool persisted, not its inner shell calls). Taking
            // it would shrink a rich live 5-row trace to 1 row mid-
            // view ("in the end state it lost all the run commands").
            // Only adopt the DB trace when it's at least as complete
            // as what we already have; otherwise keep the live fold.
            // Block path owns rendering — legacy toolTrace is unused
            // there; force it empty so nothing double-renders.
            setToolTrace((prev) =>
                useBlockPath ? []
                    : (rehydratedTrace.length >= prev.length ? rehydratedTrace : prev));

            const history: Array<{
                type: 'user' | 'bot' | 'system';
                uid?: string;
                content: {
                    text?: string,
                    node?: React.ReactNode;
                    files?: SelectedFileType[]
                },
                footer?: JSX.Element | undefined;
                disabled?: boolean;
            }> = messages
                .filter((msg: any) => {
                    if (msg?.event_type === 'progress' || msg?.type === 'progress') {
                        return false;
                    }
                    // dral 2026-05-15: persisted tool/skill PROGRESS
                    // rows leak into history as ghost bubbles (the
                    // "[workspace_execution returned in 41.3s] — …" box
                    // the user keeps seeing). They carry a ``stage``
                    // and/or the legacy bracketed/arrow preview text.
                    // The dedicated interleaved trace already renders
                    // this activity — exclude these from the prose
                    // history entirely. (renderProgressNode itself was
                    // already deleted; this kills the rehydrated leak.)
                    const stage = String(msg?.stage || '');
                    if ([
                        'tool_call', 'tool_returned',
                        'skill_tool_call', 'skill_tool_returned',
                        'thinking', 'planning', 'autopilot_summary',
                    ].includes(stage)) {
                        return false;
                    }
                    const c = typeof msg?.content === 'string' ? msg.content.trim() : '';
                    // "[<skill> returned in 1.2s] — …" / "[<skill>:
                    // <tool> returned in …]" legacy progress preview.
                    if (/^\[[^\]]+\breturned in\s+[\d.]+\s*s\]/.test(c)) {
                        return false;
                    }
                    // "<skill>: <tool> → $ cmd" / "<skill> → …" legacy
                    // skill-tool dispatch preview.
                    if (/^[a-z_]+(?::\s*[a-z_]+)?\s+→\s/.test(c)) {
                        return false;
                    }
                    // dral 2026-05-15: the backend appends a
                    // "GUI context for this turn:" block (working dir +
                    // attached dirs + "use these exact paths directly")
                    // to the user's message before sending it to the
                    // model. A copy of that AUGMENTED input leaked into
                    // rehydrated history as a ghost ASSISTANT bubble
                    // ("uh, here is the bug"). An assistant/system row
                    // carrying that boilerplate is purely an internal
                    // prompt artefact → drop it. A USER row may be the
                    // persisted augmented question → keep it (the GUI
                    // block is stripped in .map below so the bubble
                    // shows only the real question).
                    const hasGuiCtx =
                        c.includes('GUI context for this turn:') ||
                        c.includes('Working directory from GUI context:');
                    if (hasGuiCtx && mapBackendRoleToHistoryType(msg?.role) !== 'user') {
                        return false;
                    }
                    // dral 2026-05-15: Phase F persisted ``tool_call``
                    // rows feed the inline ToolCallTrace reducer above
                    // — they must NOT also be rendered as ghost bubbles
                    // in the history list. Pre-fix every tool dispatch
                    // showed as an empty bubble with a floating copy
                    // icon, producing a column of orphan icons above
                    // the assistant prose.
                    if (msg?.type === 'tool_call' || msg?.event_type === 'tool_call') {
                        return false;
                    }
                    if (msg?.role === 'tool') {
                        return false;
                    }

                    // Keep only messages that are displayable in chat history.
                    const text = typeof msg?.content === 'string' ? msg.content.trim() : '';
                    const hasFiles = Array.isArray(msg?.files) && msg.files.length > 0;
                    const role = mapBackendRoleToHistoryType(msg?.role);

                    if (role === 'user') {
                        return !!text || hasFiles;
                    }

                    return !!text || hasFiles;
                })
                .map((msg: any) => {
                    // Strip the internal "GUI context for this turn:"
                    // block so a persisted augmented user prompt shows
                    // only the real question (never the boilerplate).
                    let text = msg.content;
                    if (typeof text === 'string') {
                        text = text
                            .split(/\n+\s*GUI context for this turn:/)[0]
                            .split(/\n+\s*Working directory from GUI context:/)[0]
                            .trimEnd();
                    }
                    return {
                        type: mapBackendRoleToHistoryType(msg.role),
                        content: { text, files: msg?.files }
                    }
                });

            // When the server returned an ordered-block transcript the
            // chat renders from it; the legacy history/progress are
            // forced empty so they don't double-render beneath it.
            setHistory(useBlockPath ? [] : history);
            setProgressItems(useBlockPath ? [] : progressList);
            setLoading(false);
            current_thread_idRef.current = conversation_message_result.conversation.thread_id;

            // dral 2026-05-16 (Bug 1a): do NOT repopulate the bottom
            // input file-picker on reopen/reload. The pinned working-
            // directory pill is per-NEXT-turn intent — the user may
            // retarget their workspace in the next turn, so a new /
            // reloaded chat must start with NOTHING pinned at the
            // input. (Replaces the earlier #5 durable+Redis restore,
            // which wrongly made the bottom pill sticky across new
            // chats — screenshot 3.) The in-context attachment pills
            // the user wants persisted are the per-MESSAGE pills shown
            // INSIDE each sent user bubble, restored from the block
            // transcript itself (Bug 1b, Chat.tsx) — never from the
            // input picker.

            // dral + engineer 2026-05-15 (Redis-backed resume): probe
            // the backend to find out whether this thread is STILL
            // actively streaming. If yes, the orchestrator is mid-
            // turn — open a fresh SSE subscription so the user sees
            // new events without sending a follow-up. If no, fall
            // through to history-only (current behavior).
            //
            // Pre-fix the user had to type something to wake the live
            // stream back up, so a chat where the agent was still
            // running when the browser closed appeared "frozen" on
            // reload until the user nudged it.
            try {
                const subscribeRes = await ProtomiaApi.subscribeThread(
                    conversation_message_result.conversation.thread_id,
                );
                if (subscribeRes?.is_active && subscribeRes.session_id) {
                    setAitomiaState('running');
                    setSessionId(subscribeRes.session_id);
                }
                // Bug 1a (2026-05-16): intentionally NO setFixedFileList
                // here — the bottom input picker must not be rehydrated
                // from the Redis working_directory cache; a reopened
                // chat starts with nothing pinned so the user can
                // retarget their workspace freely.
            } catch (subErr) {
                // Best-effort: subscribe-probe failure must NOT
                // block history rendering. Logged and ignored.
                console.warn('subscribeThread probe failed (non-fatal):', subErr);
            }

            return history;
        } catch (error) {
            setLoading(false);
            console.error('Failed to fetch conversation messages:', error);
            message.error(t('smart.fetch_messages_failed') + (error as Error).message);
            return [];
        }

    }, [])

    // dral 2026-05-16 (Issue C): GUARDED post-final reconcile. When a
    // turn truly ends, the live render (history + toolTrace via
    // InterleavedTurn) has known artifacts the persisted block render
    // does NOT: a duplicated final, an echoed-prompt "planning"
    // bubble, an under-counted summary ("Coded 1 action" vs the
    // complete "Ran N commands, …"). The user confirmed the RELOAD
    // (block-transcript) render is the clean, correct one. So after
    // the turn ends we fetch the persisted transcript and, ONLY when
    // it actually contains the finished turn's assistant answer, swap
    // to it — making the live view identical to a reload (one answer,
    // one consistent summary, no echo bubble, pills intact). If the
    // answer isn't persisted yet we DON'T touch the live render
    // (retry once); never drop a turn, never fragment mid-stream.
    // Max polls before giving up (keep live render; no regression).
    // Persistence-settle time was measured live at ~5.5s..~12s and
    // can spike under load; ~700ms * 50 ≈ 35s gives generous margin
    // so the post-final reconcile reliably lands (worst case it just
    // keeps the working live render — no flicker, no fragment).
    const _RECONCILE_MAX_ATTEMPTS = 50;
    const reconcileAfterTurnEnd = async (attempt = 0) => {
        const threadId = current_thread_idRef.current;
        if (!threadId || turnReconcileBusyRef.current) return;
        // HARD GATE: only ever reconcile after a TRUE turn-final
        // (lastFinalAnswerRef is set ONLY on event_type==='final').
        // Sub-step 'completed' events also reach the scheduler; if
        // we proceeded here without a captured final we'd adopt a
        // partial transcript MID-TURN and clobber the live render on
        // every step → the persistent flicker + user-message shift.
        // No final captured ⇒ this is not the turn end ⇒ do nothing.
        if (!String(lastFinalAnswerRef.current || '').trim()) {
            turnReconcileTimerRef.current = null;
            return;
        }
        turnReconcileBusyRef.current = true;
        let scheduledRetry = false;
        try {
            const res = await ProtomiaApi.getConversationMessages(threadId);
            const blocks: any[] = Array.isArray(res?.blocks) ? res.blocks : [];
            // Concatenate every assistant text block AFTER the last
            // user block — this is the persisted answer for the turn
            // that just ended. The persisted store is written
            // progressively, so early reads yield a SHORT / fragmented
            // version. We have ground truth: the COMPLETE final answer
            // from the SSE 'final' event (lastFinalAnswerRef). Adopt
            // ONLY when the persisted answer is at least as complete —
            // deterministic, never a mid-write fragment, no flicker.
            let lastUserPos = -1;
            for (const b of blocks) {
                if (b?.type === 'text' && b?.role === 'user'
                    && typeof b?.position === 'number' && b.position > lastUserPos) {
                    lastUserPos = b.position;
                }
            }
            const persistedAnswer = blocks
                .filter((b: any) => b?.type === 'text' && b?.role === 'assistant'
                    && typeof b?.position === 'number' && b.position > lastUserPos)
                .sort((a: any, b: any) => a.position - b.position)
                .map((b: any) => String(b?.text || ''))
                .join('')
                .trim();
            // finalAnswer is guaranteed non-empty here (hard-gated at
            // function entry). "Complete enough" = persisted length
            // >= 98% of the known-complete final answer (allows
            // trailing-whitespace / normalisation diffs). NO fallback
            // to "any persisted text" — that fallback is exactly what
            // let a mid-turn call adopt a partial render.
            const finalAnswer = String(lastFinalAnswerRef.current || '').trim();
            const complete =
                persistedAnswer.length >= Math.floor(finalAnswer.length * 0.98);
            if (complete && persistedAnswer.length > 0) {
                // Adopt the clean persisted render — exactly what a
                // manual reload shows. React 18 batches these into ONE
                // commit (we're in a timer/promise) → atomic swap, no
                // intermediate frame, no flicker.
                setBlockTranscript({
                    blocks,
                    postAbsence: res?.post_absence ?? null,
                    transcriptAvailable: !!res?.transcript_available,
                });
                blockChatActiveRef.current = blocks.length > 0;
                setHistory([]);
                setToolTrace([]);
                setProgressItems([]);
            } else if (attempt < _RECONCILE_MAX_ATTEMPTS) {
                // Not fully persisted yet — DON'T touch the live render
                // (no fragment, no flicker); poll again shortly.
                scheduledRetry = true;
                turnReconcileBusyRef.current = false;
                turnReconcileTimerRef.current = setTimeout(
                    () => { reconcileAfterTurnEnd(attempt + 1); }, 700);
                return;
            }
            // else: gave up — live render stays (user can reload; no
            // regression vs. the pre-Issue-C behaviour).
        } catch (e) {
            console.warn('reconcileAfterTurnEnd failed (non-fatal):', e);
        } finally {
            turnReconcileBusyRef.current = false;
            // Terminal outcome (adopted, gave up, or errored) — clear
            // the pending-timer guard so the heartbeat / reconnect
            // resync (genuine backend-restart rehydration) is allowed
            // again. Kept set ONLY across the retry-poll window.
            if (!scheduledRetry) {
                turnReconcileTimerRef.current = null;
            }
        }
    };

    const scheduleTurnEndReconcile = () => {
        // Only arm after a TRUE turn-final (lastFinalAnswerRef is set
        // exclusively on event_type==='final'). The caller also fires
        // on sub-step status==='completed'; ignoring those here keeps
        // the reconcile timer (and the syncConversationMessages
        // defer-guard) from being held active for the whole turn and
        // prevents any mid-turn reconcile attempt.
        if (!String(lastFinalAnswerRef.current || '').trim()) {
            return;
        }
        if (turnReconcileTimerRef.current) {
            clearTimeout(turnReconcileTimerRef.current);
        }
        // Short initial settle, then the completeness-gated poll above
        // takes over (it will not adopt until the persisted answer
        // matches the known-complete final text — so a slightly-early
        // first read simply polls again, never shows a fragment).
        turnReconcileTimerRef.current = setTimeout(
            () => { reconcileAfterTurnEnd(0); }, 800);
    };
    // dral 2026-05-16 (viewer architecture): the post-final reconcile
    // is DEAD CODE — the server-authoritative ordered-block snapshot
    // is now the single source of truth, so there is nothing to
    // reconcile. Retained (unreferenced) for one release as a fast
    // revert path; this ``void`` keeps the strict noUnusedLocals build
    // green without re-introducing it into the runtime call graph.
    void scheduleTurnEndReconcile;

    const syncConversationMessages = async () => {
        const threadId = current_thread_idRef.current;
        if (!threadId || isSyncingConversationRef.current) {
            return;
        }
        // Issue C (proper): while a completeness-gated post-final
        // reconcile is pending/running, it is the SINGLE authority
        // for swapping in the persisted render. A competing
        // heartbeat / SSE-reconnect resync here would call
        // getConversationMessages WITHOUT the completeness gate and
        // could adopt a still-mid-write transcript → exactly the
        // flicker + fragmented reply. Defer to the reconcile.
        if (turnReconcileBusyRef.current || turnReconcileTimerRef.current) {
            return;
        }

        isSyncingConversationRef.current = true;
        pendingSSEMessagesRef.current = [];
        try {
            await getConversationMessages(threadId, true);
        } finally {
            isSyncingConversationRef.current = false;

            // Replay SSE messages that arrived while the DB sync was in progress
            const buffered = pendingSSEMessagesRef.current.splice(0);
            for (const data of buffered) {
                const eventId = Number(data?.event_id);
                const hasEventId = Number.isFinite(eventId);
                const currentLastId = lastEventIdRef.current;

                // Skip duplicates already covered by the DB fetch
                if (hasEventId && typeof currentLastId === 'number' && eventId <= currentLastId) {
                    continue;
                }

                if (hasEventId) {
                    lastEventIdRef.current = eventId;
                }

                if (data?.message) {
                    if (data?.event_type === 'progress' && data?.progress) {
                        setProgressItems((prev) => [...prev, data.progress]);
                    } else {
                        setHistory((prev) => {
                            const last = prev[prev.length - 1];
                            if (last && last.type === 'bot' && last.streaming) {
                                // Replace with the latest (longer) version of the message
                                const updated = [...prev];
                                updated[updated.length - 1] = {
                                    ...last,
                                    content: {
                                        ...last.content,
                                        text: data.message || '',
                                        files: data?.files ?? last.content.files,
                                    },
                                };
                                return updated;
                            }
                            return [...prev, {
                                type: 'bot' as const,
                                streaming: true,
                                content: { text: data.message, files: data?.files },
                            }];
                        });
                    }

                    if (data?.options && Array.isArray(data.options) && data.options.length > 0) {
                        setHistory((prev) => [...prev, {
                            type: 'system',
                            content: { options: data.options },
                        }]);
                    }
                }

                if (data?.status) {
                    setAitomiaState(data.status);
                    if (['end', 'completed', 'error'].includes(data.status)) {
                        setHistory(prev => {
                            const last = prev[prev.length - 1];
                            if (last && last.type === 'bot' && last.streaming) {
                                const updated = [...prev];
                                updated[updated.length - 1] = { ...last, streaming: false };
                                return updated;
                            }
                            return prev;
                        });
                        eventSourceRef.current?.close();
                        eventSourceRef.current = null;
                        setSessionId(undefined);
                        setProgressItems([]);
                        setToolTrace([]);
                        break;
                    }
                }
            }
        }
    }

    const onSaveDialog = useCallback(async () => {
        let token = localStorage.getItem('token');
        setLoading(true);
        let history: Array<{
            type: 'user' | 'bot' | 'system' | 'human' | 'assistant';
            uid?: string;
            content: {
                text?: string,
                node?: React.ReactNode;
            },
            footer?: JSX.Element | undefined;
            disabled?: boolean;
        }> = [];

        try {

            if (!current_thread_idRef.current) {
                return;
            }

            const conversation_message_result = await ProtomiaApi.getConversationMessages(current_thread_idRef.current);

            if (!conversation_message_result) {
                throw new Error('Network response was not ok');
            }

            if (!conversation_message_result?.messages?.length) {
                message.error(t('smart.no_messages'));
                setLoading(false);
                return;
            }

            history = conversation_message_result.messages.map((msg: any) => {
                return { type: msg.role, content: { text: msg.content } }
            });

            // 将历史记录转换为 markdown 格式
            const markdownLines: string[] = [];
            markdownLines.push(`# ${conversation_message_result.conversation.title || 'Conversation Export'}`);
            markdownLines.push(`\n**Export Date:** ${new Date().toLocaleString()}`);
            markdownLines.push(`\n---\n`);

            history.forEach((item, index) => {
                if (item.type === 'human') {
                    markdownLines.push(`## User\n`);
                    markdownLines.push(`${item.content.text || ''}\n`);
                } else if (item.type === 'assistant') {
                    markdownLines.push(`## Assistant\n`);
                    markdownLines.push(`${item.content.text || ''}\n`);
                } else if (item.type === 'system') {
                    markdownLines.push(`## System\n`);
                    markdownLines.push(`${item.content.text || ''}\n`);
                }

                if (index < history.length - 1) {
                    markdownLines.push(`\n---\n`);
                }
            });

            const markdownContent = markdownLines.join('\n');
            const markdownFileName = (conversation_message_result.conversation.title || 'conversation')
                .replace(/[^a-zA-Z0-9_]+/g, '_')
                .replace(/^_+|_+$/g, '') + '.md';

            // 保存数据并显示文件夹选择对话框
            setPendingSaveData({
                content: markdownContent,
                fileName: markdownFileName
            });
            setShowSaveDialog(true);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error('Failed to fetch conversation messages:', error);
            message.error(t('smart.fetch_messages_failed') + (error as Error).message);
            setLoading(false);
        }
    }, [])

    const handleSaveToSelectedFolder = async (selectedPath: string) => {
        if (!pendingSaveData) return;

        setSaveChatLoading(true);
        setShowSaveDialog(false);

        if (selectedPath === '') {
            if (baseHome) {
                selectedPath = baseHome
            } else {
                return
            }

        }

        try {
            const res = await fileService.createFile(selectedPath, pendingSaveData.fileName, pendingSaveData.content);
            if (res?.ok) {
                message.success(t('smart.export_success'));
            } else {
                message.error(t('smart.export_failed') + (res?.message || t('smart.unknown_error')));
            }
        } catch (error) {
            console.error('Failed to save conversation:', error);
            message.error(t('smart.save_failed') + (error as Error).message);
        } finally {
            setSaveChatLoading(false);
            setSelectedSavePath('')
            setPendingSaveData(null);
        }
    }

    const renameConversation = useCallback(async (thread_id: string, new_title: string) => {
        try {
            const rename_result = await ProtomiaApi.renameConversation(thread_id, new_title);

            if (!rename_result) {
                throw new Error('Network response was not ok');
            }
            return true;
        } catch (error) {
            console.error('Failed to rename conversation:', error);
            return false;
        }
    }, [])

    const deleteConversation = useCallback(async (thread_id: string) => {
        try {
            const delete_result = await ProtomiaApi.deleteConversation(thread_id);

            if (!delete_result) {
                throw new Error('Network response was not ok');
            }
            return true;
        } catch (error) {
            console.error('Failed to rename conversation:', error);
            return false;
        } finally {
            setAitomiaState(undefined);
        }

    }, [])

    // const shutDownHandler = useCallback(async () => {
    //     try {
    //         setLoading(true);
    //         const res = await jobApi.getJobs({ page: 1, pageSize: 1, jobName: 'aitomia_slurm.sh', state: jobStateEnum.RUNNING.toString() });
    //         if (res?.ok && res.data && res.data.total > 0) {
    //             const jobId = res.data.jobs[0].idJob;
    //             const res2 = await jobApi.cancelJob(jobId);
    //             if (res2?.ok) {
    //                 message.success(t('smart.job_cancel_success'));
    //                 setIsShutDownManually(true); // 标记为手动关闭
    //                 setTimeout(async () => {
    //                     const data = await getCurrentLLMJob();
    //                     if (!data) {
    //                         setLLMState(undefined);
    //                     }
    //                     setLoading(false);
    //                 }, 1000)
    //             } else {
    //                 message.error(res2?.message ?? t('smart.job_cancel_failed'));
    //             }
    //         }
    //     } catch {
    //         setLoading(false);
    //     }
    // }, [getCurrentLLMJob, setLLMState])


    const [currentWorkingPath, setCurrentWorkingPath] = useState<string | undefined>();
    const [currentWorkingPathType, setCurrentWorkingPathType] = useState<'file' | 'folder' | undefined>();
    useEffect(() => {
        setCurrentWorkingPath(activePanes[0]?.path);
        setCurrentWorkingPathType('file')
    }, [activePanes?.[0]?.path]);

    useEffect(() => {
        if (activeFolderPath) {
            setCurrentWorkingPath(activeFolderPath);
            setCurrentWorkingPathType('folder');
        }

    }, [activeFolderPath])

    const tokenUsagePercent = useMemo(() => {
        if (!billingStatus?.current_amount_micros || !billingStatus?.weekly_limit_micros) {
            return null;
        }
        return ((billingStatus.current_amount_micros / billingStatus.weekly_limit_micros) * 100).toFixed(2);
    }, [billingStatus])
    const upgradeMarkdown = t_copy('upgrade_to_pro', { defaultValue: '' });

    const onMdClick = useCallback(async (e: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
        const target = e.target as HTMLElement;
        const anchor = target.tagName === 'A'
            ? target as HTMLAnchorElement
            : target.closest('a');

        if (!anchor) {
            return;
        }

        const action = anchor.getAttribute('data-action')
            || anchor.getAttribute('action');

        if (!action) {
            return;
        }

        e.preventDefault();

        if (action.startsWith("addRole_")) {
            const role = action.replace("addRole_", "");
            const res = await JoinRole(role);
            if (res?.ok) {
                modal.success({ content: t('smart.join_role_success') });
            } else if (res?.httpStatus === 409) {
                modal.info({ content: t('smart.join_role_already_applied') });
            } else {
                modal.error({ content: t('smart.join_role_failed') });
            }
        }
    }, [])


    const handleClose = useCallback(() => setShowAitomia(false), [setShowAitomia]);

    const chatConfig: any = useMemo(() => ({
        aitomicShow: aitomicShow,
        onClose: handleClose,
        currentWorkingPath,
        currentWorkingPathType,
        setCurrentWorkingPath,
        onSaveCurrentWorkingPath,
        onReset,
        onSwitchThread,
        onStop: stopWorkflow,
        onChat,
        onSaveDialog,
        fixedFileList,
        setFixedFileList,
        // onShutDown: shutDownHandler,
        history,
        openFolderOrFileHandler: openFolderOrFile,
        onExport,
        getConversationList,
        getConversationMessages,
        renameConversation,
        deleteConversation,
        state: aitomiaState,
        progressItems,
        // dral 2026-05-15: inline tool-call trace replaces the
        // floating progressItems panel. progressItems stays for
        // one release of backwards compat with history rehydration.
        toolTrace,
        toolTraceSummary: summariseToolTrace(toolTrace),
        blockTranscript,
        userMsgs,
        baseUserBlocks,
        onSelectContext,
        conversationRefreshKey,
        autopilotState,
        asyncWorkChips,
        onAutopilotCommand,
    }), [

        aitomicShow,
        handleClose,
        currentWorkingPath,
        currentWorkingPathType,
        onSaveCurrentWorkingPath,
        onReset,
        onSwitchThread,
        stopWorkflow,
        onChat,
        onSaveDialog,
        fixedFileList,
        // shutDownHandler,
        history,
        openFolderOrFile,
        onExport,
        getConversationList,
        getConversationMessages,
        renameConversation,
        deleteConversation,
        aitomiaState,
        progressItems,
        toolTrace,
        blockTranscript,
        userMsgs,
        baseUserBlocks,
        onSelectContext,
        conversationRefreshKey,
        autopilotState,
        asyncWorkChips,
        onAutopilotCommand,
    ])
    if (!aitomicShow) {
        return null;
    }

    if (loading) {
        return (
            <div className="relative flex flex-col w-full h-full justify-center items-center">
                <Spin size="large" />
            </div>
        );
    }

    const handleRetryChatLoad = () => {
        setChatLoaded(false);
        setChatFailed(false);
        setChatFailKind('load');
        setShowUpgradePanel(false);
        setChatRetryKey(prev => prev + 1);
    };


    // chatConfig (legacy AitomiaChat props bag) is intentionally
    // retained but unreferenced now — keep noUnusedLocals happy
    // without unpicking its large dependency graph during the swap.
    void chatConfig;
    return <div className="w-full h-full flex flex-col">
        <ChatErrorBoundary key={chatRetryKey} onError={(err?: unknown) => {
            // Which failure is this? The boundary catches BOTH "the remote never loaded" and
            // "the remote loaded fine and then threw" -- e.g. the gateway restarted under a tab
            // that had been working. Until now both rendered the module-load copy, which blames
            // remoteEntry.js; on 2026-08-26 that sent two people hunting a CDN fault while
            // /chat_atom/ was serving 250 requests HTTP 200 and other users' turns were
            // completing. `chatLoaded` is the discriminator and MUST be read BEFORE it is reset
            // -- resetting it first is what destroyed the evidence.
            setChatFailKind(chatLoaded ? 'backend' : 'load');
            if (err) console.error('[chat] subtree error', err);
            setChatLoaded(false);
            setChatFailed(true);
        }}>
            <React.Suspense fallback={
                <div className="flex flex-1 justify-center items-center py-8">
                    <Spin size="large" />
                </div>
            }>
                {!chatFailed && (
                    <>
                        <ChatMinimal
                            getRawFileResource={getRawFileResourceInBlob}
                            currentWorkingPath={currentWorkingPath}
                            currentWorkingPathType={currentWorkingPathType}
                            fixedFileList={fixedFileList}
                            llmModelList={llmModelList}
                            setFixedFileList={setFixedFileList}
                            onClose={handleClose}
                            onSaveChat={(content, fileName) => {
                                setPendingSaveData({ content, fileName });
                                setShowSaveDialog(true);
                            }}
                            onOpenPath={openFolderOrFile}
                            onQuotaExceeded={async (callback?: (tokenBillingStatus?: CurrentMonthTokenBillingStatus) => void) => {
                                const tokenBillingStatus = await getCurrentMonthTokenBillingStatus();
                                setBillingStatus(tokenBillingStatus);
                                callback?.(tokenBillingStatus);
                            }}
                        />
                        <ChatReadySignal onReady={() => setChatLoaded(true)} />
                    </>
                )}
            </React.Suspense>
        </ChatErrorBoundary>
        {chatFailed && (
            <div className="flex flex-1 items-center justify-center px-6 py-10">
                <div className="w-full max-w-lg">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 dark:bg-[#3C1F2F] text-rose-500 dark:text-[#F48771]">
                        <WarningOutlined className="text-[22px]" />
                    </div>
                    <div className="space-y-3">
                        <h3 className="m-0 text-2xl font-semibold text-slate-900 dark:text-[#CCCCCC]">
                            {t(chatFailKind === 'backend' ? 'smart.chat_backend_error_title' : 'smart.chat_load_error_title')}
                        </h3>
                        <p className="m-0 text-sm leading-6 text-slate-600 dark:text-[#999999]">
                            {t(chatFailKind === 'backend' ? 'smart.chat_backend_error' : 'smart.chat_load_error')}
                        </p>
                        <p className="m-0 text-sm leading-6 text-slate-500 dark:text-[#858585]">
                            {t(chatFailKind === 'backend' ? 'smart.chat_backend_error_hint' : 'smart.chat_load_error_hint')}
                        </p>
                    </div>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <Button
                            type="primary"
                            size="large"
                            icon={<ReloadOutlined />}
                            onClick={handleRetryChatLoad}
                            className="!rounded-xl !border-0 !bg-slate-900 dark:!bg-[#1E1E1E] !px-5 !shadow-none hover:!bg-slate-800 dark:hover:!bg-[#252526]"
                        >
                            {t('smart.chat_retry')}
                        </Button>
                        <span className="text-xs leading-5 text-slate-400 dark:text-[#858585]">
                            {t('smart.chat_load_error_contact')}
                        </span>
                    </div>
                </div>
            </div>
        )}
        {chatLoaded && !chatFailed && !!billingStatus?.weekly_limit_micros && (() => {
            const usageRatio = (billingStatus?.current_amount_micros ?? 0) / billingStatus.weekly_limit_micros;
            const isOverLimit = usageRatio > 1;
            const resetAt = new Date(billingStatus.next_reset_at ?? 0).toLocaleString();
            const tooltipTitle = isOverLimit
                ? (isProUser
                    ? t('smart.billing_over_limit_pro') + ' ' + resetAt
                    : t('smart.billing_over_limit_free') + ' ' + resetAt)
                : (t('smart.billing_status_tooltip', { percent: tokenUsagePercent ?? 0 }) + ' ' + resetAt);
            return (
                <div id="percentage" className="w-full px-4 pb-2 text-sm flex items-center gap-2">
                    <Tooltip title={tooltipTitle}>
                        <div className="flex-1">
                            <Progress
                                percent={Math.min(usageRatio * 100, 100)}
                                showInfo={false}
                                strokeColor={isOverLimit ? 'red' : (usageRatio > 0.8 ? 'orange' : '')}
                            />
                        </div>
                    </Tooltip>
                    {isProUser ? (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-50 dark:bg-[#1F3A3A] text-cyan-600 dark:text-[#3794DE] flex-shrink-0">
                            <SafetyOutlined className="text-base" />
                            <span className="text-xs font-medium uppercase tracking-wide">Pro</span>
                        </div>
                    ) : (
                        <Tooltip title={t('smart.upgrade_tooltip', 'Upgrade to Pro')}>
                            <Button
                                size="small"
                                type="dashed"
                                icon={<RocketOutlined />}
                                className="flex-shrink-0"
                                onClick={() => setShowUpgradePanel(v => !v)}
                            >
                                {t('smart.upgrade_button', 'Upgrade')}
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title={t('smart.guide', 'Guide')}>
                        <Button size="small" type="text" onClick={() => openTour("protomia")}><QuestionCircleOutlined /></Button>
                    </Tooltip>
                    <Tooltip title={t('smart.feedback_tooltip', 'Send Feedback')}>
                        <Button
                            size="small"
                            type="text"
                            icon={<BugOutlined />}
                            className="flex-shrink-0"
                            onClick={() => setShowFeedback(true)}
                        />
                    </Tooltip>
                </div>
            );
        })()}
        <FeedbackModal visible={showFeedback} onClose={() => setShowFeedback(false)} />
        {chatLoaded && !chatFailed && showUpgradePanel && (
            <div
                className="fixed bottom-4 right-4 z-50 w-80 rounded-lg shadow-xl border border-cyan-200 dark:border-[#0E639C] bg-white dark:bg-[#252526] backdrop-blur-sm flex flex-col"
                style={{ maxHeight: '70vh' }}
            >
                <div className="flex items-center justify-between px-4 py-2 border-b border-cyan-100 dark:border-[#0E639C] bg-gradient-to-r from-cyan-500 to-teal-500 rounded-t-lg">
                    <span className="font-semibold text-white dark:text-[#FFFFFF] flex items-center gap-1">
                        <SafetyOutlined className="text-white dark:text-[#FFFFFF]" />
                        {t('smart.upgrade_panel_title', 'Upgrade to Pro')}
                    </span>
                    <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        className="text-white dark:text-[#FFFFFF] hover:text-cyan-100 dark:hover:text-[#4DB8FF]"
                        onClick={() => setShowUpgradePanel(false)}
                    />
                </div>
                <div className="p-4 overflow-y-auto text-sm text-slate-700 dark:text-[#CCCCCC]" style={{ flex: 1 }}>
                    {upgradeMarkdown ? (
                        <MarkdownRender content={upgradeMarkdown} onClick={onMdClick}>
                        </MarkdownRender>
                    ) : (
                        <p className="text-cyan-500 dark:text-[#3794DE] text-center">{t('smart.upgrade_panel_empty', 'No content available.')}</p>
                    )}
                </div>
            </div>
        )}
        <Modal
            title="Select Folder to Save Conversation"
            open={showSaveDialog}
            onCancel={() => {
                setShowSaveDialog(false);
                setPendingSaveData(null);
                setSelectedSavePath('');
            }}
            width={600}
            destroyOnHidden
            okButtonProps={{ hidden: true }}
            cancelButtonProps={{ hidden: true }}
        >
            <div className="mb-4">
                <p className="text-gray-600 dark:text-[#999999] mb-2">File name: <span className="font-semibold">{pendingSaveData?.fileName}</span></p>
                <p className="text-gray-600 dark:text-[#999999] mb-4">Select a folder to save the conversation:</p>
                <p className="text-sm text-gray-500 dark:text-[#999999]">Current Selected Path: <span className="font-mono">{selectedSavePath || 'None'}</span></p>
            </div>
            <div style={{ height: '400px', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                <FolderSelector
                    onSelectDirectory={setSelectedSavePath}
                    initialPath={baseHome ?? fileService.baseHome}
                />
            </div>
            <div className=' flex justify-end mt-4'>
                <Space>
                    <Button loading={saveChatLoading} disabled={!selectedSavePath} onClick={() => handleSaveToSelectedFolder(selectedSavePath)}>Save</Button>
                    <Button loading={saveChatLoading} onClick={() => handleSaveToSelectedFolder('')}>{t('smart.save_in_home')}</Button>
                </Space>
            </div>
        </Modal>
    </div>
}

export default Chat;
